import {t} from '@lingui/core/macro';

import {idToIri, iriToId} from 'shared/api/iriHelper';

import IServerAlertRule from '../model/ServerAlertRule/IServerAlertRule';
import CreateServerAlertRuleType from '../model/ServerAlertRule/CreateServerAlertRuleType';
import IServerAlertRuleFormState from '../model/ServerAlertRule/IServerAlertRuleFormState';

import {normalizeServerAlertRuleOperator} from './serverAlertRuleHelper';

export const ACCOUNT_SCOPE_VALUE = 'account';
const SERVER_ALERT_RULE_SECONDS_IN_MINUTE = 60;
export const SERVER_ALERT_RULE_MIN_FOR_MINUTES = 5;
export const SERVER_ALERT_RULE_MIN_RECOVER_MINUTES = 1;

const DEFAULT_FOR_SECONDS = 600;
const DEFAULT_RECOVER_SECONDS = 300;

export interface IServerAlertRuleFormError {
    field: 'for_minutes' | 'recover_minutes' | 'scope',
    message: string,
}

export function getServerAlertRuleFormDefaults(
    item?: IServerAlertRule,
    presetServerUuid?: string,
): IServerAlertRuleFormState {
    return {
        server: item?.server
            ? iriToId(item.server)
            : (presetServerUuid ?? ACCOUNT_SCOPE_VALUE),
        metric: item?.metric,
        scope: item?.scope ?? '',
        operator: normalizeServerAlertRuleOperator(item?.operator ?? '>'),
        threshold: item?.threshold ?? 0,
        for_minutes: (item?.for_seconds ?? DEFAULT_FOR_SECONDS) / SERVER_ALERT_RULE_SECONDS_IN_MINUTE,
        recover_minutes: (item?.recover_seconds ?? DEFAULT_RECOVER_SECONDS) / SERVER_ALERT_RULE_SECONDS_IN_MINUTE,
        severity: item?.severity ?? 'warning',
        enabled: item?.enabled ?? true,
    } as IServerAlertRuleFormState;
}

export function validateServerAlertRuleForm(
    formData: IServerAlertRuleFormState,
    requiresScope: boolean,
): IServerAlertRuleFormError[] {
    const errors: IServerAlertRuleFormError[] = [];

    if (formData.for_minutes < SERVER_ALERT_RULE_MIN_FOR_MINUTES) {
        errors.push({field: 'for_minutes', message: t`Значение не может быть меньше 5 минут`});
    }

    if (formData.recover_minutes < SERVER_ALERT_RULE_MIN_RECOVER_MINUTES) {
        errors.push({field: 'recover_minutes', message: t`Значение не может быть меньше 1 минуты`});
    }

    if (requiresScope && !formData.scope) {
        errors.push({field: 'scope', message: t`Для этой метрики нужно указать точку монтирования`});
    }

    if (!requiresScope && formData.scope) {
        errors.push({field: 'scope', message: t`Точка монтирования указывается только для метрик файловой системы`});
    }

    return errors;
}

export function buildServerAlertRulePayload(
    formData: IServerAlertRuleFormState,
    requiresScope: boolean,
): CreateServerAlertRuleType {
    return {
        server: formData.server === ACCOUNT_SCOPE_VALUE ? null : idToIri(formData.server, 'servers'),
        metric: formData.metric as IServerAlertRule['metric'],
        scope: requiresScope ? formData.scope : null,
        operator: formData.operator,
        threshold: Number(formData.threshold),
        for_seconds: Math.round(Number(formData.for_minutes) * SERVER_ALERT_RULE_SECONDS_IN_MINUTE),
        recover_seconds: Math.round(Number(formData.recover_minutes) * SERVER_ALERT_RULE_SECONDS_IN_MINUTE),
        severity: formData.severity,
        enabled: formData.enabled,
    };
}
